/**
 * DepartmentPerformanceChart Component
 * Bar chart showing department-level performance comparison
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card } from '../../common';
import { Building2 } from 'lucide-react';
import type { DepartmentPerformanceSummary } from '../../../types';
import { getScoreColor } from '../../../types';

export interface DepartmentPerformanceChartProps {
  departments: DepartmentPerformanceSummary[];
  title?: string;
  onDepartmentClick?: (departmentId: string) => void;
  className?: string;
  isLoading?: boolean;
}

export const DepartmentPerformanceChart: React.FC<DepartmentPerformanceChartProps> = ({
  departments,
  title = 'Department Performance',
  onDepartmentClick,
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-48 mb-6" />
          <div className="h-64 bg-stone-100 rounded" />
        </div>
      </Card>
    );
  }

  if (!departments || departments.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-12 text-stone-400">
          <Building2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No department data available</p>
        </div>
      </Card>
    );
  }

  // Prepare chart data
  const chartData = departments
    .map((dept) => ({
      name: dept.departmentName,
      score: Math.round(dept.averageScore),
      departmentId: dept.departmentId,
      memberCount: dept.memberCount,
    }))
    .sort((a, b) => b.score - a.score);

  const handleBarClick = (data: { departmentId: string }) => {
    if (onDepartmentClick && data.departmentId) {
      onDepartmentClick(data.departmentId);
    }
  };

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
        <span className="text-sm text-stone-500">{departments.length} departments</span>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tick={{ fontSize: 12, fill: '#78716c' }}
              axisLine={{ stroke: '#d6d3d1' }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12, fill: '#57534e' }}
              axisLine={false}
              tickLine={false}
              width={90}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white border border-stone-200 rounded-lg shadow-lg p-3">
                      <p className="font-medium text-stone-800">{data.name}</p>
                      <p className="text-sm text-stone-600">
                        Score: <span className="font-bold" style={{ color: getScoreColor(data.score) }}>{data.score}</span>
                      </p>
                      <p className="text-xs text-stone-500">{data.memberCount} members</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar
              dataKey="score"
              radius={[0, 4, 4, 0]}
              cursor={onDepartmentClick ? 'pointer' : 'default'}
              onClick={handleBarClick}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default DepartmentPerformanceChart;
